import type { ReactNode } from 'react';

import { StockAvailabilityChip } from '~/components/common/chips';
import { Badge } from '~/components/ui/badge';
import { getAvailability } from '~/features/onboarding/utils/product-detail';
import { cn } from '~/utils/cn';

/** Badge classes for the availability states the binary chip can't express —
 *  preorder (info) and any other recognised-but-non-binary value (neutral). */
const FALLBACK_BADGE_CLASSES = {
  info: 'border-info/20 bg-info-50 text-info',
  neutral: 'border-border bg-muted text-muted-foreground',
} as const;

/**
 * Availability display shared by the products table + card. Renders the common
 * StockAvailabilityChip for in-stock / out-of-stock, and a tone-matched Badge
 * for the states the binary chip can't express (preorder → info, unknown →
 * neutral). Falls back to `emptyFallback` when the row carries no availability.
 */
export function ProductAvailabilityChip({
  availability,
  className = '',
  emptyFallback = null,
}: {
  availability: string | null | undefined;
  className?: string;
  emptyFallback?: ReactNode;
}) {
  const { label, tone } = getAvailability(availability);

  if (tone === 'success') return <StockAvailabilityChip status="inStock" className={className} />;
  if (tone === 'warning')
    return <StockAvailabilityChip status="outOfStock" className={className} />;
  if (!label) return <>{emptyFallback}</>;

  // Only preorder (info) and unknown (neutral) reach here — success/warning
  // already returned the chip above.
  return (
    <Badge
      variant="outline"
      className={cn(
        'pointer-events-none w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
        FALLBACK_BADGE_CLASSES[tone === 'info' ? 'info' : 'neutral'],
        className,
      )}
    >
      {label}
    </Badge>
  );
}
